import { Fragment, useEffect, useMemo, useState } from "react";
import {
  getUsageDetail,
  getUsageSummary,
  type UsageDetailRow,
  type UsageDetailFilters,
  type UsageSummary,
} from "../lib/api";
import Button from "../ui/Button";
import Spinner from "../ui/Spinner";

type DatePreset = "month" | "last_month" | "last_7" | "all";

function fmtMoney(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function fmtUnits(
  platform: string,
  input: number | null,
  output: number | null
): string {
  if (platform === "anthropic") {
    const i = input ?? 0;
    const o = output ?? 0;
    return `${i.toLocaleString()} in / ${o.toLocaleString()} out`;
  }
  if (platform === "kie") return "1 image";
  if (platform === "tavily") return `${input ?? 1} search`;
  return "-";
}

function datesForPreset(preset: DatePreset): { from?: string; to?: string } {
  const now = new Date();
  if (preset === "all") return {};
  if (preset === "last_7") {
    const from = new Date(now.getTime() - 7 * 86400_000);
    return { from: from.toISOString() };
  }
  if (preset === "last_month") {
    const start = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
    );
    const end = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
    );
    return { from: start.toISOString(), to: end.toISOString() };
  }
  // month (default)
  const start = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  );
  return { from: start.toISOString() };
}

export default function UsagePanel() {
  const [preset, setPreset] = useState<DatePreset>("month");
  const [platform, setPlatform] = useState<string>("");
  const [taskFilter, setTaskFilter] = useState<string>("");
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [rows, setRows] = useState<UsageDetailRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const filters: UsageDetailFilters = useMemo(
    () => ({
      ...datesForPreset(preset),
      platform: platform || undefined,
      task: taskFilter || undefined,
      limit: 300,
    }),
    [preset, platform, taskFilter]
  );

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [sum, det] = await Promise.all([
        getUsageSummary(),
        getUsageDetail(filters),
      ]);
      setSummary(sum);
      setRows(det.rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, platform, taskFilter]);

  const uniqueTasks = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.task);
    return Array.from(set).sort();
  }, [rows]);

  const windowTotal = rows.reduce((s, r) => s + r.cost_usd, 0);

  return (
    <section className="bg-white border border-ink-100 rounded-xl overflow-hidden shadow-sm shadow-ink-900/5">
      <header className="px-6 py-5 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-ink-800">
            Usage
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Per-call cost breakdown across AI, image generation, and
            research.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load}>
          Refresh
        </Button>
      </header>

      <div className="p-6 space-y-6">
        {/* Summary strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              label="This month"
              value={fmtMoney(summary.month_to_date_usd)}
              sub={`${summary.call_count} calls`}
            />
            <SummaryCard
              label="Avg per blog"
              value={fmtMoney(summary.avg_blog_cost_usd)}
              sub={`${summary.blog_count_this_month} blog${
                summary.blog_count_this_month === 1 ? "" : "s"
              } this month`}
            />
            <SummaryCard
              label="AI + review"
              value={fmtMoney(summary.by_platform.anthropic ?? 0)}
              sub="Claude Sonnet + Haiku"
            />
            <SummaryCard
              label="Images + search"
              value={fmtMoney(
                (summary.by_platform.kie ?? 0) +
                  (summary.by_platform.tavily ?? 0)
              )}
              sub={`${fmtMoney(summary.by_platform.kie ?? 0)} img / ${fmtMoney(
                summary.by_platform.tavily ?? 0
              )} search`}
            />
          </div>
        )}

        {/* Filter row */}
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value as DatePreset)}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-sm"
          >
            <option value="month">This month</option>
            <option value="last_month">Last month</option>
            <option value="last_7">Last 7 days</option>
            <option value="all">All time</option>
          </select>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-sm"
          >
            <option value="">All platforms</option>
            <option value="anthropic">Anthropic</option>
            <option value="kie">KIE (images)</option>
            <option value="tavily">Tavily (search)</option>
          </select>
          <select
            value={taskFilter}
            onChange={(e) => setTaskFilter(e.target.value)}
            className="px-3 py-1.5 border border-ink-200 rounded-md text-sm"
          >
            <option value="">All tasks</option>
            {uniqueTasks.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <span className="text-sm text-ink-500 ml-auto">
            Window total: <strong>{fmtMoney(windowTotal)}</strong>{" "}
            ({rows.length} rows)
          </span>
        </div>

        {/* Detail table */}
        {err && <p className="text-sm text-red-600">{err}</p>}
        {loading && !rows.length ? (
          <div className="flex items-center gap-2 text-ink-500 py-8">
            <Spinner size={14} /> Loading...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-ink-500 border-b border-ink-100">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Platform</th>
                  <th className="py-2 pr-3">Task</th>
                  <th className="py-2 pr-3">Model</th>
                  <th className="py-2 pr-3">Units</th>
                  <th className="py-2 pr-3 text-right">Cost</th>
                  <th className="py-2 pr-3">Blog</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr
                      className="border-b border-ink-100/70 hover:bg-sand-50 cursor-pointer"
                      onClick={() =>
                        setExpanded((prev) => ({
                          ...prev,
                          [r.id]: !prev[r.id],
                        }))
                      }
                    >
                      <td className="py-2 pr-3 text-ink-500 tabular-nums">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3">
                        <PlatformBadge p={r.platform} />
                      </td>
                      <td className="py-2 pr-3 font-mono text-[12px] text-ink-700">
                        {r.task}
                      </td>
                      <td className="py-2 pr-3 text-ink-600">
                        {r.model ?? "-"}
                      </td>
                      <td className="py-2 pr-3 text-ink-600">
                        {fmtUnits(r.platform, r.input_units, r.output_units)}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium tabular-nums">
                        {fmtMoney(r.cost_usd)}
                      </td>
                      <td className="py-2 pr-3 text-ink-600 truncate max-w-[200px]">
                        {r.blog_title ?? "-"}
                      </td>
                    </tr>
                    {expanded[r.id] && r.meta && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-2 px-3 bg-sand-50 text-xs text-ink-600"
                        >
                          <pre className="whitespace-pre-wrap break-words font-mono">
                            {JSON.stringify(r.meta, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-8 text-center text-ink-500"
                    >
                      No usage in this window yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-ink-100 bg-sand-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
        {label}
      </p>
      <p className="font-display text-2xl font-bold text-ink-800 tabular-nums mt-1 leading-none">
        {value}
      </p>
      <p className="text-xs text-ink-500 mt-1.5">{sub}</p>
    </div>
  );
}

function PlatformBadge({ p }: { p: string }) {
  const style: Record<string, string> = {
    anthropic: "bg-brand-50 text-brand-700 border-brand-200",
    kie: "bg-amber-50 text-amber-800 border-amber-200",
    tavily: "bg-sky-50 text-sky-800 border-sky-200",
  };
  const label: Record<string, string> = {
    anthropic: "Anthropic",
    kie: "KIE",
    tavily: "Tavily",
  };
  return (
    <span
      className={[
        "inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded border",
        style[p] ?? "bg-ink-50 text-ink-700 border-ink-200",
      ].join(" ")}
    >
      {label[p] ?? p}
    </span>
  );
}
